import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CartItem } from 'types/cart';
import { formatCurrency } from 'utils/formatters';
import { getLazyImageProps, getProductImageUrl } from 'utils/productImage';
import { hasProductPhoto } from 'utils/productWatermark';
import ProductWatermarkOverlay from 'components/catalog/ProductWatermarkOverlay';
import { ROUTES } from 'config/routes';
import { useAuthContext } from 'context/AuthContext';
import { useCatalogPresentationOptional } from 'context/CatalogPresentationContext';
import StockLevelChip from 'components/catalog/StockLevelChip';
import { CustomerDiscountChips, OfferBadge, useOfferLook } from 'components/catalog/OfferBadge';
import { applyCustomerCascade, customerPercentsOf } from 'utils/customerDiscount';
import { hasDiscount } from 'utils/offerAppearance';
import { maxOrderQuantity } from 'utils/stockLevel';
import CartQuantityStepper from './CartQuantityStepper';

interface CartLineItemProps {
  item: CartItem;
  currency?: string;
  compact?: boolean;
  disabled?: boolean;
  increaseDisabled?: boolean;
  onQuantityChange?: (quantity: number) => void;
  onRemove?: () => void;
  removeLabel?: string;
  decreaseLabel?: string;
  increaseLabel?: string;
  quantityLabel?: string;
}

const productPath = (item: CartItem) => {
  const slug = item.slug?.trim() && item.slug !== 'item' ? item.slug : item.productId;
  return ROUTES.PRODUCT.replace(':slug', encodeURIComponent(slug));
};

const CartLineItem: React.FC<CartLineItemProps> = ({
  item,
  currency = 'ARS',
  compact = false,
  disabled = false,
  increaseDisabled = false,
  onQuantityChange,
  onRemove,
  removeLabel = 'Eliminar',
  decreaseLabel,
  increaseLabel,
  quantityLabel,
}) => {
  const { t } = useTranslation('cart');
  const { customer } = useAuthContext();
  const thumbSize = compact ? 56 : 80;
  const sku = item.productId?.trim();
  const presentation = useCatalogPresentationOptional();
  const offerLook = useOfferLook();
  const percents = item.noBonificable ? [] : customerPercentsOf(customer);
  const offer = hasDiscount(item.unitPrice, item.listPrice, item.discountPercent);
  const hasCustomer = percents.length > 0 && item.unitPrice > 0;
  const finalUnit = hasCustomer ? applyCustomerCascade(item.unitPrice, percents) : item.unitPrice;
  const referenceUnit = offer ? item.listPrice! : item.unitPrice;
  const showStrike = Boolean(offerLook.showListPrice && (offer || hasCustomer) && referenceUnit > finalUnit);
  const lineTotal = finalUnit * item.quantity;
  const lineReference = referenceUnit * item.quantity;
  const priceColor = (offer || hasCustomer) && offerLook.priceColor ? offerLook.priceColor : undefined;
  const showOfferChip = offer && offerLook.badgeVisible;
  const showCustomerChips = hasCustomer && offerLook.customerBadgeVisible;
  const maxQty = maxOrderQuantity({ stock: item.availableQty }, presentation?.allowBackorders ?? true);
  const imageProps = getLazyImageProps(
    getProductImageUrl(item.imageUrl, item.productId),
    item.name
  );

  return (
    <Box
      sx={{
        display: 'flex',
        gap: compact ? 1.5 : 2,
        alignItems: 'flex-start',
        py: compact ? 1.25 : 2,
      }}
    >
      <Box
        component={RouterLink}
        to={productPath(item)}
        sx={{
          width: thumbSize,
          height: thumbSize,
          flexShrink: 0,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Box
          component="img"
          {...imageProps}
          sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0.5 }}
        />
        <ProductWatermarkOverlay active={hasProductPhoto(item.imageUrl)} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component={RouterLink}
          to={productPath(item)}
          variant={compact ? 'body2' : 'body1'}
          fontWeight={600}
          color="text.primary"
          sx={{
            textDecoration: 'none',
            display: '-webkit-box',
            WebkitLineClamp: compact ? 2 : 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            '&:hover': { color: 'primary.main' },
          }}
        >
          {item.name}
        </Typography>

        {sku ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {t('sku', { code: sku })}
          </Typography>
        ) : null}

        {!compact && item.quantity !== 1 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {showStrike && (
              <Box component="span" sx={{ textDecoration: 'line-through', mr: 0.75 }}>
                {formatCurrency(referenceUnit, currency)}
              </Box>
            )}
            <Box component="span" sx={priceColor ? { color: priceColor } : undefined}>
              {formatCurrency(finalUnit, currency)} c/u
            </Box>
          </Typography>
        )}
        {(showOfferChip || showCustomerChips) && (
          <Box
            data-testid="cart-discount-row"
            sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}
          >
            {showOfferChip ? <OfferBadge percent={item.discountPercent!} /> : null}
            {showCustomerChips ? <CustomerDiscountChips percents={percents} /> : null}
          </Box>
        )}
        {item.stockLevel ? (
          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
            {!compact && <StockLevelChip level={item.stockLevel} quantity={item.availableQty} />}
            {item.hasBackorderUnits ? (
              <Typography variant="caption" color="text.secondary">
                Incluye unidades a pedido
              </Typography>
            ) : null}
          </Box>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mt: compact ? 1 : 1.5,
            flexWrap: 'wrap',
          }}
        >
          {onQuantityChange ? (
            <CartQuantityStepper
              quantity={item.quantity}
              disabled={disabled}
              increaseDisabled={increaseDisabled || item.canOrder === false}
              max={maxQty}
              size={compact ? 'small' : 'medium'}
              decreaseLabel={decreaseLabel}
              increaseLabel={increaseLabel}
              quantityLabel={quantityLabel ?? t('quantity')}
              onChange={onQuantityChange}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              × {item.quantity}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ textAlign: 'right' }}>
              {showStrike && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textDecoration: 'line-through', display: 'block' }}
                >
                  {formatCurrency(lineReference, currency)}
                </Typography>
              )}
              <Typography
                variant={compact ? 'body2' : 'subtitle1'}
                fontWeight={700}
                sx={priceColor ? { color: priceColor } : undefined}
              >
                {formatCurrency(lineTotal, currency)}
              </Typography>
            </Box>
            {onRemove && (
              <IconButton
                size="small"
                aria-label={removeLabel}
                disabled={disabled}
                onClick={onRemove}
                sx={{ color: 'text.secondary' }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CartLineItem;
